import { useEffect, useState, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AdminLayout from "@/components/admin/AdminLayout"
import { getAdminBannerDetail, createAdminBanner, updateAdminBanner } from "@/api/adminBannerApi"
import { getS3PresignedUrl } from "@/api/s3Api"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

export default function AdminBannerFormPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditMode = !!id

    const [loading, setLoading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [imageFile, setImageFile] = useState<File | null>(null)
    const [imagePreview, setImagePreview] = useState<string>("")

    const [form, setForm] = useState({
        bannerType: "HERO",
        title: "",
        description: "",
        linkUrl: "",
        badgeText: "",
        sortOrder: 1,
        isActive: "Y",
        imageUrl: "" // DB에 저장된 S3 URL
    })

    // 수정 모드일 때 기존 데이터 불러오기
    useEffect(() => {
        if (isEditMode) {
            const fetchDetail = async () => {
                try {
                    const response = await getAdminBannerDetail(id!)
                    const data = response.data || response
                    setForm(data)
                    setImagePreview(data.imageUrl) // 기존 이미지 미리보기 세팅
                } catch (error) {
                    console.error(error)
                    alert("배너 정보를 불러오는데 실패했습니다.")
                    navigate("/admin/banners")
                }
            }
            fetchDetail()
        }
    }, [id, navigate, isEditMode])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
    }

    // 이미지 파일 선택 핸들러
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setImageFile(file)
            setImagePreview(URL.createObjectURL(file)) // 로컬 미리보기
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!form.title) return alert("타이틀을 입력해주세요.")
        if (!isEditMode && !imageFile) return alert("배너 이미지를 등록해주세요.")

        try {
            setLoading(true)

            // 1. 새 이미지가 첨부되었다면 S3에 업로드
            let finalImageUrl = form.imageUrl
            if (imageFile) {
                const { presignedUrl, imageUrl } = await getS3PresignedUrl(imageFile.name, "banners")
                await fetch(presignedUrl, {
                    method: "PUT",
                    body: imageFile,
                    headers: { "Content-Type": imageFile.type },
                })
                finalImageUrl = imageUrl
            }

            // 2. DB 저장 (생성 or 수정)
            const payload = { ...form, imageUrl: finalImageUrl }

            if (isEditMode) {
                await updateAdminBanner(id!, payload)
                alert("배너가 성공적으로 수정되었습니다.")
            } else {
                await createAdminBanner(payload)
                alert("배너가 성공적으로 등록되었습니다.")
            }
            navigate("/admin/banners")

        } catch (error) {
            console.error(error)
            alert("배너 저장 중 오류가 발생했습니다.")
        } finally {
            setLoading(false)
        }
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div>
                    <h2 className="text-3xl font-bold">{isEditMode ? "배너 수정" : "신규 배너 등록"}</h2>
                </div>

                <Card className="max-w-4xl rounded-[28px] border border-slate-200 shadow-sm">
                    <CardHeader>
                        <CardTitle className="text-xl">배너 상세 정보</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="grid gap-6 md:grid-cols-2">

                            {/* 💡 이미지 업로드 영역 */}
                            <div className="md:col-span-2 space-y-2">
                                <Label>배너 이미지 <span className="text-red-500">*</span></Label>
                                <div
                                    className="flex h-64 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 overflow-hidden hover:bg-slate-100 transition-colors"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {imagePreview ? (
                                        <img src={imagePreview} alt="미리보기" className="h-full w-full object-cover" />
                                    ) : (
                                        <div className="text-center text-slate-500">
                                            <p className="font-medium">클릭하여 이미지를 업로드하세요</p>
                                            <p className="text-sm mt-1">(권장: HERO 1200x600, PROMO 800x200)</p>
                                        </div>
                                    )}
                                </div>
                                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
                            </div>

                            <div className="space-y-2">
                                <Label>배너 타입</Label>
                                <select name="bannerType" value={form.bannerType} onChange={handleChange} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none">
                                    <option value="HERO">메인 상단 배너 (HERO)</option>
                                    <option value="PROMO">하단 프로모션 배너 (PROMO)</option>
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label>노출 여부</Label>
                                <select name="isActive" value={form.isActive} onChange={handleChange} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none">
                                    <option value="Y">노출함 (Y)</option>
                                    <option value="N">숨김 (N)</option>
                                </select>
                            </div>

                            <div className="space-y-2 md:col-span-2">
                                <Label>메인 타이틀 <span className="text-red-500">*</span></Label>
                                <Input name="title" value={form.title} onChange={handleChange} className="h-12 rounded-xl" placeholder="예: 식품부터 화장품까지, 자주 사는 상품을 더 쉽게" />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label>서브 텍스트</Label>
                                <Input name="description" value={form.description} onChange={handleChange} className="h-12 rounded-xl" placeholder="메인 타이틀 아래에 들어갈 부가 설명" />
                            </div>

                            <div className="space-y-2">
                                <Label>연결 링크 (URL)</Label>
                                <Input name="linkUrl" value={form.linkUrl} onChange={handleChange} className="h-12 rounded-xl" placeholder="/products/1 또는 외부 링크" />
                            </div>
                            <div className="space-y-2 flex gap-4">
                                <div className="flex-1 space-y-2">
                                    <Label>상단 뱃지 텍스트</Label>
                                    <Input name="badgeText" value={form.badgeText} onChange={handleChange} className="h-12 rounded-xl" placeholder="예: 믿고 사는 데일리 쇼핑몰" />
                                </div>
                                <div className="w-24 space-y-2">
                                    <Label>정렬 순서</Label>
                                    <Input type="number" name="sortOrder" value={form.sortOrder} onChange={handleChange} className="h-12 rounded-xl" min="1" />
                                </div>
                            </div>

                            <div className="md:col-span-2 flex justify-end gap-3 mt-6 border-t border-slate-100 pt-6">
                                <Button type="button" variant="outline" className="rounded-xl h-12 px-6" onClick={() => navigate("/admin/banners")} disabled={loading}>
                                    취소
                                </Button>
                                <Button type="submit" disabled={loading} className="rounded-xl h-12 px-8 bg-slate-900 hover:bg-slate-800 text-white">
                                    {loading ? "저장 중..." : "배너 저장하기"}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    )
}